
import { useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { Loader2 } from "lucide-react";
import { GalleryPhoto } from "./types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DeleteConfirmDialogProps {
  photo: GalleryPhoto | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onPhotoDeleted: () => void;
}

export function DeleteConfirmDialog({
  photo,
  isOpen,
  onOpenChange,
  onPhotoDeleted
}: DeleteConfirmDialogProps) {
  const { language } = useLanguage();
  const [isDeleting, setIsDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!photo) return;
    
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('gallery_photos')
        .delete()
        .eq('id', photo.id);
        
      if (error) throw error;
      
      toast.success(t('gallery.photoDeletedSuccess', language) || 'Photo deleted successfully');
      onPhotoDeleted();
    } catch (error) {
      console.error("Error deleting photo:", error);
      toast.error(t('gallery.photoDeleteFailed', language) || 'Failed to delete photo');
    } finally {
      setIsDeleting(false);
      onOpenChange(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('gallery.deletePhoto', language) || 'Delete Photo'}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('gallery.deletePhotoConfirm', language) || 'Are you sure you want to delete this photo? This action cannot be undone.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel', language) || 'Cancel'}</AlertDialogCancel>
          <AlertDialogAction onClick={confirmDelete} disabled={isDeleting}>
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('gallery.deleting', language) || 'Deleting...'}
              </>
            ) : (
              t('common.delete', language) || 'Delete'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
